import type { OwnerProfileResource } from "../../owner-profile";
import type { NotificationChannel } from "../../marketing-company";
import type { MarketplaceResource } from "../../marketplace";

export type NotificationTemplateEmailContent = {
  subject: string;
  body_html: string;
  body_plain: string;
  signature_id: string;
  signature: NotificationSignatureResource | null;
};

export type NotificationTemplateSmsContent = {
  body_plain: string;
};

export type NotificationTemplateWhatsappContent = {
  external_id: string;
  variables: Record<string, string>;
};

export enum NotificationRouteTypeEnum {
  Birthday = "birthday",
  OrderReceived = "order_received",
  OrderShipped = "order_shipped",
  Invoice = "invoice",
  Credit = "credit",
  Offer = "offer",
  Listing = "listing",
  Estimate = "estimate",
  ProformaInvoice = "proforma_invoice",
  DeliveryNote = "delivery_note",
  DeliveryNoteCancellation = "delivery_note_cancellation",
  PaymentReminder = "payment_reminder",
  Reminder = "reminder",
  OrderConfirmation = "order_confirmation",
  Calculation = "calculation",
  Letter = "letter",
  PaymentAdvice = "payment_advice",
  SupplierOrder = "supplier_order",
}

export type NotificationSignatureResource = {
  id: string;
  title: string;
  html_text: string;
  plain_text: string;
  created_at: string;
  updated_at: string | null;
};

export type CommonNotificationTemplateResourceProps = {
  id: string;
  title: string;
  type: string;
  owner_profiles?: OwnerProfileResource[];
  created_at: string | null;
  updated_at: string | null;
};

export type EmailNotificationTemplateResource = {
  channel: NotificationChannel.Email;

  content: NotificationTemplateEmailContent;
} & CommonNotificationTemplateResourceProps;

export type SmsNotificationTemplateResource = {
  channel: NotificationChannel.Sms;

  content: NotificationTemplateSmsContent;
} & CommonNotificationTemplateResourceProps;

export type WhatsAppNotificationTemplateResource = {
  channel: NotificationChannel.WhatsApp;

  content: NotificationTemplateWhatsappContent;
} & CommonNotificationTemplateResourceProps;

export type NotificationTemplateResource =
  | EmailNotificationTemplateResource
  | SmsNotificationTemplateResource
  | WhatsAppNotificationTemplateResource;

export type NotificationDirection = "outgoing" | "incoming";

export enum NotificationDeliveryStatus {
  Pending = "pending",
  Processing = "processing",
  Accepted = "accepted",
  Sent = "sent",
  Delivered = "delivered",
  Read = "read",
  Failed = "failed",
  Fallback = "fallback",
}

export type NotificationMessageResource = {
  id: string;
  channel: NotificationChannel;
  direction: NotificationDirection;
  status: NotificationDeliveryStatus;
  recipient: string;
  subject: string | null;
  /** Subject or the beginning of the text, at most 120 characters. */
  preview: string;
  error_message: string | null;
  notification_type: string | null;
  transport: string | null;
  tracking_id: string | null;
  send_attempts: number;
  scheduled_at: string | null;
  schedule_reason: string | null;
  expires_at: string | null;
  sent_at: string | null;
  created_at: string;
  setting?: { id: string; name: string; transport: string } | null;
  owner_profile?: { id: string; title: string } | null;
  customer?: { id: string; number: string | null; name: string | null } | null;
  route?: { id: string; title: string } | null;
  /** Card only. */
  text?: string | null;
  /** Card only. */
  body_html?: string | null;
};

export type NotificationTransportFieldType =
  | "text"
  | "password"
  | "email"
  | "number"
  | "boolean"
  | "select"
  | "ports";

export type NotificationTransportField = {
  key: string;
  type: NotificationTransportFieldType;
  required: boolean;
  /** Never returned by the API, only whether it is stored. */
  secret: boolean;
  default: string | number | boolean | null;
  choices: string[];
};

export type NotificationTransportDescription = {
  key: string;
  channels: NotificationChannel[];
  credentials: NotificationTransportField[];
  options: NotificationTransportField[];
};

export type NotificationSettingResource = {
  id: string;
  name: string;
  channel: NotificationChannel;
  transport: string;
  owner_profile_id: string | null;
  owner_profile?: { id: string; title: string } | null;
  is_active: boolean;
  /** Managed by the server configuration: read-only in the interface. */
  is_system: boolean;
  priority: number;
  daily_limit: number | null;
  limit_type: "daily" | "rolling" | null;
  send_from: string | null;
  send_until: string | null;
  cooldown_min_seconds: number | null;
  cooldown_max_seconds: number | null;
  cooldown_until: string | null;
  /** Decided by the server: the pause time comes without a time zone. */
  is_paused: boolean;
  consecutive_failures: number;
  /** Switched off by the server after too many failures, not by a person. */
  disabled_by_failures: boolean;
  last_failure_at: string | null;
  /** Non-secret credential values only. */
  credentials: Record<string, string | number | boolean>;
  /** Encrypted with another key: must be entered again. */
  credentials_unreadable: boolean;
  /** Secret credential keys that are stored. */
  credentials_set: string[];
  options: Record<string, unknown>;
  sent_today: number;
  /** Card only. */
  has_messages?: boolean;
  created_at: string;
  updated_at: string;
};

export type NotificationRouteResource = {
  id: string;
  is_active: boolean;
  type: string;
  title: string;
  owner_profiles?: OwnerProfileResource[];
  channels?: NotificationRouteChannelResource[];
  created_at: string | null;
  updated_at: string | null;
};

export type NotificationRouteChannelResource = {
  id: string;
  channel: NotificationChannel;
  priority: number;
  allows_attachments: boolean;
  is_active: boolean;
  template_id: string;
  template?: NotificationTemplateResource;
  excluded_marketplaces?: MarketplaceResource[];
};
